import React, { useMemo } from 'react';
import { Table } from 'syngenta-digital-cropwise-react-ui-kit';
import useTableScrollConfig from 'screens/Applications&WorkOrders/Components/DetailsTabs/hooks/useTableScrollConfig';
import { OPERATION_DETAILS_TABS_WRAPPER_SELECTOR } from 'screens/Applications&WorkOrders/Components/DetailsTabs/constants';
import { LinkedRecord } from 'screens/Applications&WorkOrders/WorkOrders/Hooks/linkedRecords.types';
import getLinkedRecordsColumns from './columns';

export const LINKED_RECORDS_PAGE_SIZE = 20;

const LINKED_RECORDS_TABLE_OFFSET_EM = 39.75;
const LINKED_RECORDS_TABLE_LEADING_HEIGHT = 8;

/**
 * Module scope, not inline in the pagination config: a component declared inside
 * another is a fresh type each render, so React remounts instead of updating.
 */
const renderPaginationTotal = (total: number, range: [number, number]) => (
  <span className="text-neutral-60">
    {range[0]}-{range[1]} of {total}
  </span>
);

interface LinkedRecordsTableProps {
  records: LinkedRecord[];
  totalRecords: number;
  page: number;
  onPageChange: (page: number) => void;
  onViewMore: (recordId: string) => void;
}

const LinkedRecordsTable = ({
  records,
  totalRecords,
  page,
  onPageChange,
  onViewMore
}: LinkedRecordsTableProps) => {
  const columns = useMemo(() => getLinkedRecordsColumns(onViewMore), [
    onViewMore
  ]);

  const tableScrollConfig = useTableScrollConfig({
    rowCount: records.length,
    desktopOffsetEm: LINKED_RECORDS_TABLE_OFFSET_EM,
    leadingHeight: LINKED_RECORDS_TABLE_LEADING_HEIGHT,
    wrapperSelector: OPERATION_DETAILS_TABS_WRAPPER_SELECTOR
  });

  const hasPaginationNavigation = totalRecords > LINKED_RECORDS_PAGE_SIZE;

  const pagination = useMemo(
    () => ({
      current: page + 1,
      pageSize: LINKED_RECORDS_PAGE_SIZE,
      total: totalRecords,
      showSizeChanger: false,
      position: ['bottomRight'],
      showTotal: renderPaginationTotal,
      onChange: (nextPage: number) => onPageChange(nextPage - 1)
    }),
    [page, totalRecords, onPageChange]
  );

  return (
    <div data-testid="linked-records-table" className="px-6 py-8">
      <div
        className={`application-view-products-tab-table ${
          hasPaginationNavigation
            ? ''
            : 'application-view-products-tab-table-no-pagination'
        }`}
      >
        <Table
          columns={columns}
          scroll={tableScrollConfig}
          pagination={pagination as any}
          dataSource={records}
          rowKey="id"
        />
      </div>
    </div>
  );
};

export default LinkedRecordsTable;
